import "./CreditReference.scss";
import React, { useEffect, useState } from "react";
import { joiResolver } from "@hookform/resolvers/joi";
import { DatePicker, Input, Button, Row, Col } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import _pick from "lodash/pick";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { Currency } from "../../../common/components/Input/Input";
import { showErrorPopUp } from "../../../common/components/ShowNotification/showNotification";
import { MOMENT_YEAR_MONTH_FORMAT } from "../../../common/contstants/date";
import { processError } from "../../../common/utils/error";
import {
  CREDIT_REFERENCE_DEFAULT_VALUE,
  FORM_FIELD_LABELS,
} from "../../constants/creditReference";
import CreditRefService from "../../services/CreditRefService";
import {
  CreditRefData,
  CreditRefAttributes,
} from "../../types/CreditReference";
import {
  creditRefFields,
  creditRefValidationSchema,
} from "../../validations/credit-reference";

type CreditReferenceProps = {
  refData?: CreditRefAttributes;
  onSubmitSuccess?: (arg: CreditRefAttributes, note: string) => void;
};

const CreditReference: React.FunctionComponent<CreditReferenceProps> = ({
  onSubmitSuccess,
  refData,
}: CreditReferenceProps) => {
  const { assessmentId } = useParams<{
    assessmentId: string;
  }>();
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(creditRefValidationSchema),
    defaultValues: CREDIT_REFERENCE_DEFAULT_VALUE,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (refData) {
      const fields = Object.keys(_pick(refData, creditRefFields)) as [
        keyof CreditRefData
      ];
      fields.forEach((field) => {
        setValue(field, refData[field] as string);
      });
    }
  }, [refData, setValue]);

  const onSubmit = async (
    creditRef: Record<keyof CreditRefData, string> & { notes: string }
  ) => {
    setIsLoading(true);
    const creditRefData: CreditRefData = {
      ...creditRef,
      naf: +creditRef.naf,
      balloon: +creditRef.balloon,
      paymentAmount: +creditRef.paymentAmount,
      outstandingBalance: +creditRef.outstandingBalance,
    };

    try {
      let creditRefDoc;
      if (!refData?.id) {
        const { data } = await CreditRefService.create(
          assessmentId as string,
          creditRefData
        );
        creditRefDoc = data;
      } else {
        const { data } = await CreditRefService.update(
          refData.id,
          assessmentId as string,
          creditRefData
        );
        creditRefDoc = data;
      }

      reset();
      if (onSubmitSuccess)
        onSubmitSuccess(creditRefDoc, creditRef?.notes as string);
    } catch (e) {
      processError(e, (errorMessage) => {
        showErrorPopUp(errorMessage);
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="credit-ref" data-testid="credit-ref-form">
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={[24, 8]}>
          <Col span={12}>
            <div className="field-label">{FORM_FIELD_LABELS.bank}</div>
            <Controller
              name="bank"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Input
                  {...register("bank")}
                  name="bank"
                  value={value as string | number}
                  onChange={(e) => onChange(e.target.value)}
                  autoComplete="off"
                  className={classNames("ant-input", {
                    invalid: errors?.bank,
                  })}
                  data-testid="bank"
                />
              )}
            />
            <div className="validation" data-testid="bank-error">
              {errors?.bank?.message}
            </div>
          </Col>
          <Col span={12}>
            <div className="field-label">{FORM_FIELD_LABELS.start}</div>
            <Controller
              name="start"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  value={value ? dayjs(value, MOMENT_YEAR_MONTH_FORMAT) : null}
                  onChange={(_, dateString) => onChange(dateString)}
                  format={MOMENT_YEAR_MONTH_FORMAT}
                  placeholder={MOMENT_YEAR_MONTH_FORMAT.toLowerCase()}
                  picker="month"
                  className={classNames({
                    invalid: errors?.start,
                  })}
                  data-testid="start"
                />
              )}
            />
            <div className="validation" data-testid="start-error">
              {errors?.start?.message}
            </div>
          </Col>
          <Col span={12}>
            <div className="field-label">
              {FORM_FIELD_LABELS.term} (in months)
            </div>
            <Controller
              name="term"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Input
                  {...register("term")}
                  name="term"
                  value={value as string | number}
                  onChange={(e) => onChange(e.target.value)}
                  autoComplete="off"
                  className={classNames("ant-input", {
                    invalid: errors?.term,
                  })}
                  data-testid="term"
                />
              )}
            />
            <div className="validation" data-testid="term-error">
              {errors?.term?.message}
            </div>
          </Col>
          <Col span={12}>
            <div className="field-label">{FORM_FIELD_LABELS.asset}</div>
            <Controller
              name="asset"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Input
                  {...register("asset")}
                  name="asset"
                  value={value as string | number}
                  onChange={(e) => onChange(e.target.value)}
                  autoComplete="off"
                  className={classNames("ant-input", {
                    invalid: errors?.asset,
                  })}
                  data-testid="asset"
                />
              )}
            />
            <div className="validation" data-testid="asset-error">
              {errors?.asset?.message}
            </div>
          </Col>
          <Col span={12}>
            <div className="field-label">{FORM_FIELD_LABELS.naf}</div>
            <Controller
              name="naf"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Currency
                  value={value}
                  onValueChange={({ value }) => onChange(value)}
                  className={classNames({
                    invalid: errors?.naf,
                  })}
                  data-testid="naf"
                />
              )}
            />
            <div className="validation" data-testid="naf-error">
              {errors?.naf?.message}
            </div>
          </Col>

          <Col span={12}>
            <div className="field-label">{FORM_FIELD_LABELS.paymentAmount}</div>
            <Controller
              name="paymentAmount"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Currency
                  value={value}
                  onValueChange={({ value }) => onChange(value)}
                  className={classNames({
                    invalid: errors?.paymentAmount,
                  })}
                  data-testid="payment-amount"
                />
              )}
            />
            <div className="validation" data-testid="payment-amount-error">
              {errors?.paymentAmount?.message}
            </div>
          </Col>
          <Col span={12}>
            <div className="field-label">{FORM_FIELD_LABELS.balloon}</div>
            <Controller
              name="balloon"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Currency
                  value={value}
                  onValueChange={({ value }) => onChange(value)}
                  className={classNames({
                    invalid: errors?.balloon,
                  })}
                  data-testid="balloon"
                />
              )}
            />
            <div className="validation" data-testid="balloon-error">
              {errors?.balloon?.message}
            </div>
          </Col>
          <Col span={12}>
            <div className="field-label">
              {FORM_FIELD_LABELS.outstandingBalance}
            </div>
            <Controller
              name="outstandingBalance"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Currency
                  value={value}
                  onValueChange={({ value }) => onChange(value)}
                  className={classNames({
                    invalid: errors?.outstandingBalance,
                  })}
                  data-testid="balance"
                />
              )}
            />
            <div className="validation" data-testid="balance-error">
              {errors?.outstandingBalance?.message}
            </div>
          </Col>
          <Col span={24}>
            <div className="field-label">{FORM_FIELD_LABELS.conduct}</div>
            <Controller
              name="conduct"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Input
                  {...register("conduct")}
                  name="conduct"
                  value={value as string | number}
                  onChange={(e) => onChange(e.target.value)}
                  autoComplete="off"
                  className={classNames("ant-input", {
                    invalid: errors?.conduct,
                  })}
                  data-testid="conduct"
                />
              )}
            />
            <div className="validation" data-testid="conduct-error">
              {errors?.conduct?.message}
            </div>
          </Col>
          <Col span={24}>
            <div className="field-label">{FORM_FIELD_LABELS.notes}</div>
            <Controller
              name="notes"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Input.TextArea
                  value={value}
                  onChange={onChange}
                  maxLength={500}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  showCount
                  data-testid="notes"
                />
              )}
            />
          </Col>
        </Row>
        <Button
          htmlType="submit"
          loading={isLoading}
          className="submit qf-btn-green"
          data-testid="save-btn"
        >
          Save
        </Button>
      </form>
    </div>
  );
};

export default CreditReference;
